import { isEmpty, last } from 'lodash';
import { isDashletMode } from '../auth/dashlet.util';
import { getConfig } from './global-discover-config';
import {
  gainsightConstants,
  isFramelessMfeMode,
  VisualizationLoadedSource,
} from './Constants';

declare function aptrinsic(...args: any[]): void;

// True once this module has successfully injected the Gainsight PX script.
// Set immediately after script insertion and before the identify call, so that
// a throwing identify cannot lead to a second script injection on a later call.
// track() is gated on this flag — it fires only after GainsightClient has
// reached the inject step, regardless of whether identify itself threw.
let initialized = false;

export const GainsightClient = {
  initialize(
    user: {
      id?: string;
      analyticsAccountId?: string;

      roles?: string[];
      tenant?: string;
    },
    isDashletModeFlag: boolean,
  ): void {
    // Guard: dashlet flag passed by caller, live config value, or frameless-MFE
    // (frameless MFE runs inside the Sugar page, which has its own aptrinsic tag)
    if (isDashletModeFlag || isDashletMode() || isFramelessMfeMode()) {
      return;
    }

    const envId = getConfig()?.GAINSIGHT_PX_ENV_ID;
    if (isEmpty(envId)) {
      return;
    }

    // Derive the visitor id from the IDM SRN (e.g. srn:cloud:iam::2443:user:d332b52e-...).
    // Take the segment after the last ':' — for a well-formed SRN that is the user GUID.
    // A plain id with no ':' yields the whole string; the analyticsAccountId guard below
    // already limits successful initialization to IDM tenants where the SRN is expected.
    // The visitor id sent to Gainsight must be ONLY the GUID: Sugar's own Gainsight tag
    // identifies the same person by that GUID in the same PX product, so matching it keeps
    // one visitor per person and lets their Discover and Sell activity be correlated. The
    // full SRN would create a duplicate visitor and leak the IAM account number.
    const visitorId = last((user?.id ?? '').split(':')) ?? '';
    if (isEmpty(visitorId)) {
      return;
    }

    if (isEmpty(user?.analyticsAccountId)) {
      return;
    }

    // initialized is checked before the foreign-aptrinsic guard so that
    // initialized = true (already set on a prior call) always wins.  This
    // means: even if identify threw on the first call and something replaced
    // window.aptrinsic afterward, a second initialize() is still a no-op.
    if (initialized) {
      return;
    }

    // Guard: a foreign window.aptrinsic already exists (e.g. Sugar's own tag).
    // Never overwrite it, set .p on it, or inject a second script alongside it.
    if (typeof (window as any).aptrinsic !== 'undefined') {
      return;
    }

    try {
      // Create the queuing stub before the async SDK script loads.
      const i = 'aptrinsic';
      (window as any)[i] = function (...args: any[]) {
        ((window as any)[i].q = (window as any)[i].q || []).push(args);
      };
      (window as any)[i].p = envId;

      const r = document.createElement('script');
      r.async = true;
      r.src = `https://web-sdk.aptrinsic.com/api/aptrinsic.js?a=${encodeURIComponent(envId)}`;
      const scripts = document.getElementsByTagName('script');
      if (scripts.length > 0) {
        scripts[0].parentNode.insertBefore(r, scripts[0]);
      } else {
        document.head.appendChild(r);
      }

      // Set initialized immediately after script insertion so that a throwing
      // identify call below cannot cause a second injection on a retry.
      initialized = true;
    } catch (e) {
      console.warn('GainsightClient: script injection failed', e);
      return;
    }

    try {
      const visitorPayload: Record<string, string> = { id: visitorId };
      if (!isEmpty(user.roles)) {
        visitorPayload.discoverRoles = (user.roles ?? []).join(',');
      }

      const accountPayload: Record<string, string> = {
        id: user.analyticsAccountId,
      };
      if (!isEmpty(user.tenant)) {
        accountPayload.discoverTenant = user.tenant;
      }

      aptrinsic('identify', visitorPayload, accountPayload);
    } catch (e) {
      console.warn('GainsightClient: identify failed', e);
    }
  },

  track(
    discovery: { viz?: { name?: string; id?: string } } | undefined,
    source: VisualizationLoadedSource,
  ): void {
    if (!initialized) {
      return;
    }
    try {
      aptrinsic('track', gainsightConstants.reportLoaded, {
        surface: 'standalone',
        source,
        reportName: discovery?.viz?.name,
        vizId: discovery?.viz?.id,
      });
    } catch (e) {
      console.warn('GainsightClient: track failed', e);
    }
  },

  /** Reset module state for test isolation only. */
  _resetForTest(): void {
    initialized = false;
  },
};
